import { Autocomplete, TextField, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import clsx from "clsx";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HvSubstationCreate } from "../components/hvSubstations/HvSubstationCreate";
import { AccessControlContext } from "./contexts/AccessControlContext";
import { useHVSubstationsWithCompanyAndSite } from "./data/Assets/useHVSubstationsWithCompanyAndSite";
import { CreateableDropdown, DropdownOption } from "./shared/CreateableDropdown";
import { MessageToast, ToastMode } from "./shared/MessageToast";
import { Snackbar } from "./shared/Snackbar";
import { ValidationMessage } from "./shared/ValidationMessage";
import { LOCALSTORAGE_KEYS } from "./shared/constants";
import { convertAssetToHvSubstation } from "./shared/helpers/HvSubstationHelpers";
import { Attribute } from "./shared/interfaces/Attribute";
import { HvSubstation } from "./shared/interfaces/HvSubstation";
import { HvSubstationMetadata } from "./shared/interfaces/HvSubstationMetadata";

interface Props {
  customerId?: number;
  companyId?: number;
  siteId?: number;
  onSubstationChange: (
    selectedSubstationId?: number,
    selectedSubstationName?: string,
    attributes?: Attribute[],
  ) => void;
  selectedSubstationId?: number;
  enableSelectionCaching?: boolean;
  invalid?: boolean;
  invalidMessage?: string;
  createable?: boolean;
  creationPrePopulatedValues?: Partial<HvSubstation>;
  register?: UseFormRegister<FieldValues>;
  formState?: FormState<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  showLabel?: boolean;
  getAttributes?: boolean;
  width?: string;
}

const localStorageKey = LOCALSTORAGE_KEYS.hvSubstation;

export const HVSubstationDropdown = React.forwardRef((props: Props & DropdownProps, ref: React.Ref<Dropdown>) => {
  const { t } = useTranslation();

  const accessControlContext = useContext(AccessControlContext);
  const userCanAddHvSubstation = accessControlContext.canI("add", "hvSubstations", props.customerId);

  const {
    customerId,
    companyId,
    siteId,
    selectedSubstationId,
    onSubstationChange,
    enableSelectionCaching,
    invalid,
    invalidMessage,
    createable,
    creationPrePopulatedValues,
    register,
    formState,
    setValue,
    showLabel,
    getAttributes,
    width,
  } = props;

  const substations = useHVSubstationsWithCompanyAndSite(customerId, companyId, siteId, getAttributes);
  const [hvSubstationCreatePanelOpen, setHvSubstationCreatePanelOpen] = useState(false);
  const [substationNameAutocompleteValue, setSubstationNameAutocompleteValue] = useState<string | undefined>();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  useEffect(() => {
    if (enableSelectionCaching) {
      const storedSubstationId = localStorage.getItem(localStorageKey);
      const storedMatchesSelected = storedSubstationId && selectedSubstationId === Number(storedSubstationId);

      if (!selectedSubstationId || !storedMatchesSelected) {
        storedSubstationId && onSubstationChange(Number(storedSubstationId), "");
      }
    }
  }, [selectedSubstationId, onSubstationChange, enableSelectionCaching]);

  function handleChange(e: React.SyntheticEvent, value: HvSubstationMetadata): void;
  function handleChange(e: DropdownOption): void;

  function handleChange(e: React.SyntheticEvent | DropdownOption, value?: HvSubstationMetadata): void {
    const substationId = value ? value.id : e ? parseInt((e as DropdownOption).value, 10) : undefined;

    if (substationId) {
      if (enableSelectionCaching) {
        localStorage.setItem(localStorageKey, substationId.toString());
      }

      const substation = substations.data?.find((s) => s.id === substationId);
      setValue &&
        setValue(`hvSubstation`, value || { id: substationId }, {
          shouldValidate: true,
        });
      onSubstationChange(substationId, substation?.name || "", substation?.attributes);
    } else {
      onSubstationChange(undefined, undefined, undefined);
    }
  }

  function handleCreateSubstationClick(text: string | undefined) {
    setSubstationNameAutocompleteValue(text);
    setHvSubstationCreatePanelOpen(true);
  }

  function handleNewHvSubstationPanelClose() {
    setHvSubstationCreatePanelOpen(false);
  }

  const handleHvSubstationCreated = useCallback(
    (substationId: number, substationName: string) => {
      substations.refetch().then(() => {
        onSubstationChange(substationId, substationName);
      });
      setSnackbarText(
        t("common.dataSuccessfullyCreated", {
          entity: t("substations.hvSubstation"),
          id: substationName,
        }),
      );
      setShowSnackbar(true);
      setHvSubstationCreatePanelOpen(false);
    },
    [onSubstationChange, substations, t],
  );

  function renderHVSubstationDropdown() {
    const selectedSubstation = substations.data?.find((substation) => substation.id === selectedSubstationId);
    return (
      <>
        {createable && userCanAddHvSubstation ? (
          <CreateableDropdown
            value={selectedSubstation}
            options={substations.data || []}
            onChange={handleChange}
            optionLabelField="name"
            optionValueField="id"
            placeholder={t("substations.selectSubstation")}
            createOptionLabel={t("substations.createSubstation")}
            onCreateOptionClick={handleCreateSubstationClick}
            invalid={invalid}
            key={selectedSubstationId || "undefined"}
            style={{ width: width || 300 }}
          />
        ) : register ? (
          <div>
            <Autocomplete
              {...register(`hvSubstation`, { value: selectedSubstationId, required: true })}
              disableClearable
              autoHighlight
              blurOnSelect
              className={formState && formState.errors["hvSubstation"] ? "p-invalid" : undefined}
              disabled={!customerId}
              placeholder={t("substations.selectSubstation")}
              id="hvSubstation"
              value={selectedSubstation}
              onChange={handleChange}
              options={substations.data ?? []}
              ref={ref}
              renderInput={(params) => <TextField {...params} placeholder={t("substations.selectSubstation")} />}
              renderOption={(props, option, { inputValue }) => {
                const matches = match(option.name, inputValue, {
                  insideWords: true,
                });
                const parts = parse(option.name, matches);
                return (
                  <li {...props}>
                    <div>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              }}
              size="small"
              sx={{ width: props.width || 200, bgcolor: "white" }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {formState && formState.errors["hvSubstation"] && (
              <ValidationMessage
                invalidMessage={t("common.pleaseEnter", {
                  val: "$t(substations.substation)",
                })}
                validationType="error"
                showIcon
              />
            )}
          </div>
        ) : (
          <Autocomplete
            disableClearable
            autoHighlight
            blurOnSelect
            className={clsx({ "p-invalid": invalid })}
            disabled={!customerId}
            placeholder={t("substations.selectSubstation")}
            id="hvSubstationDropdown"
            value={selectedSubstation}
            onChange={handleChange}
            options={substations.data ?? []}
            ref={ref}
            renderInput={(params) => <TextField {...params} placeholder={t("substations.selectSubstation")} />}
            renderOption={(props, option, { inputValue }) => {
              const matches = match(option.name, inputValue, {
                insideWords: true,
              });
              const parts = parse(option.name, matches);
              return (
                <li {...props}>
                  <div>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: part.highlight ? 700 : 400,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                </li>
              );
            }}
            size="small"
            sx={{ width: props.width || 200, bgcolor: "white" }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        )}

        {invalid && invalidMessage && (
          <ValidationMessage invalidMessage={invalidMessage} validationType="error" showIcon />
        )}
      </>
    );
  }
  const substationLabelTemplate = (
    <Col xs={"auto"} className="d-flex align-items-center">
      <Typography variant="subtitle2" color="textPrimary">
        {t("substations.substation")}:
      </Typography>
    </Col>
  );

  if (substations.isLoading) {
    return <CircularProgress size={24} />;
  } else if (substations.error) {
    return (
      <MessageToast
        text={t("common.dataFetchError", {
          val: "$t(substations.substations)",
        })}
        mode={ToastMode.Error}
        show
      />
    );
  } else {
    return (
      <>
        {showLabel && (
          <Row className={showLabel ? "d-flex flex-column ml-1" : ""}>
            {showLabel && substationLabelTemplate}
            <Col xs={"auto"}>{renderHVSubstationDropdown()}</Col>
          </Row>
        )}

        {!showLabel && (
          <div className="d-flex justify-content-start">
            <div className="d-flex flex-column w-100">{renderHVSubstationDropdown()}</div>
          </div>
        )}

        {customerId != null && substations.data && userCanAddHvSubstation && hvSubstationCreatePanelOpen && (
          <HvSubstationCreate
            open={hvSubstationCreatePanelOpen}
            onClose={handleNewHvSubstationPanelClose}
            customerId={customerId}
            onHvSubstationCreated={handleHvSubstationCreated}
            existingSubstations={substations.data?.map((substation) => convertAssetToHvSubstation(substation))}
            refreshSubstationData={() => substations.refetch()}
            prepopulatedHvSubstationValues={
              creationPrePopulatedValues || {
                name: substationNameAutocompleteValue,
              }
            }
          />
        )}

        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />
      </>
    );
  }
});
